// Demo header skin. Same plumbing as the default header but with a centred
// wordmark + chip-style nav buttons + a pill cart counter — shows that a
// variant can change layout, not just colours.
import { useState } from 'react';
import { Space, Badge, Grid } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import { useActiveCart } from '@core/hooks/useCart';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import Button from '@components/base/Button';
import MobileNav from '@components/base/MobileNav';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import { useCopy } from '@/app/providers/useCopy';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';
import { useIndustry } from '@/app/providers/industry-context.js';

export default function HeaderDemo() {
  const t = useCopy();
  useAppLanguage();
  const { isAuthenticated } = useAuth();
  const { lineCount } = useActiveCart();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [navOpen, setNavOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const navItems = config?.nav?.primary ?? [];
  const appName = runtimeBrandName || config?.copy?.appName || config?.name || 'Bytz';

  const onOrdersClick = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--bg-page)',
          borderBottom: '0.125rem solid var(--border-color)',
          padding: '0.75rem 1.5rem',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'auto 1fr auto' : '1fr auto 1fr',
          alignItems: 'center',
          gap: 16,
        }}
      >
        {isMobile ? null : (
          <nav style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
            {navItems.map((n) => {
              const active = pathname === n.to || pathname.startsWith(`${n.to}/`);
              return (
                <Link key={n.to} to={n.to}>
                  <Button shape="round" type={active ? 'primary' : 'default'} size="small">
                    {n.label}
                  </Button>
                </Link>
              );
            })}
          </nav>
        )}

        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifySelf: 'center',
            gap: 8,
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 22,
            letterSpacing: '0.03125rem',
            color: 'var(--color-primary)',
            textTransform: 'uppercase',
          }}
        >
          {runtimeLogoUrl || config?.logoUrl ? (
            <img
              src={runtimeLogoUrl || config.logoUrl}
              alt=""
              style={{
                width: '1.875rem',
                height: '1.875rem',
                maxWidth: '32rem',
                maxHeight: '32rem',
                objectFit: 'contain',
                background: 'transparent',
              }}
            />
          ) : null}
          {appName}
        </Link>

        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            aria-label="My Orders"
            shape="round"
            icon={<ShoppingOutlined />}
            onClick={onOrdersClick}
          />
          <Link to="/cart">
            <Badge count={lineCount} size="small">
              <Button shape="round">🛍️ Bag</Button>
            </Badge>
          </Link>
          {!isMobile ? (
            isAuthenticated ? (
              <Link to="/account">
                <Button shape="round">Account</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button shape="round" type="primary">
                  {t('signIn')}
                </Button>
              </Link>
            )
          ) : null}
        </Space>
      </header>
      <MobileNav open={navOpen} onClose={() => setNavOpen(false)} items={navItems} />
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
    </>
  );
}
